import { Record } from "@/domain/records/entities/Record";
import { RecordDomainService } from "@/domain/records/services/RecordDomainService";
import type {
  RecordPresenterPort,
  RecordSummaryViewModel,
  RecordViewModel,
} from "@/application/records/ports/RecordPresenterPort";

/**
 * Presenter for transforming Record domain objects to UI ViewModels
 * Clean Architecture - Application Layer
 */
export class RecordPresenter implements RecordPresenterPort {
  constructor(private readonly recordDomainService: RecordDomainService) {}

  /**
   * Transform domain Record to UI ViewModel
   */
  present(record: Record): RecordViewModel {
    return {
      // Basic info
      id: record.id,
      name: record.name,
      status: record.status,
      sizeMb: record.sizeMb,
      fileExtension: record.fileExtension,
      isPublic: record.isPublic,
      fileType: record.fileType,

      // Dates
      createdAt: record.createdAt?.toISOString(),
      updatedAt: record.updatedAt?.toISOString(),

      // Status info
      statusMessage: this.recordDomainService.getStatusMessage(record),
      completionPercentage:
        this.recordDomainService.getCompletionPercentage(record),
      isInProgress: record.isInProgress,
      isFinished: record.isFinished,
      hasError: record.hasError,

      // Capabilities
      canGenerateReports: this.recordDomainService.canGenerateReports(record),
      canViewRecord: this.recordDomainService.canViewRecord(record),
      supportsTranscript: this.recordDomainService.supportsFeature(
        record,
        "transcript"
      ),
      supportsReports: this.recordDomainService.supportsFeature(
        record,
        "reports"
      ),
      supportsEditing: this.recordDomainService.supportsFeature(
        record,
        "editing"
      ),
      supportsSharing: this.recordDomainService.supportsFeature(
        record,
        "sharing"
      ),

      // Available versions
      availableVersions: record.availableVersions,
      availableReportTypes: record.getAvailableReportTypes(),
      maxVersion: record.getLatestVersion("general") || 0, // Get max version across all types

      // Metadata
      participantsCount: record.participantsCount,
    };
  }

  /**
   * Transform multiple records
   */
  presentMultiple(records: Record[]): RecordViewModel[] {
    return this.recordDomainService
      .sortRecordsByPriority(records)
      .map((record) => this.present(record));
  }

  /**
   * Create summary view for record lists
   */
  presentSummary(record: Record): RecordSummaryViewModel {
    return {
      id: record.id,
      name: record.name,
      status: record.status,
      statusMessage: this.recordDomainService.getStatusMessage(record),
      completionPercentage:
        this.recordDomainService.getCompletionPercentage(record),
      isInProgress: record.isInProgress,
      isFinished: record.isFinished,
      hasError: record.hasError,
      fileType: record.fileType,
      createdAt: record.createdAt?.toISOString(),
      availableReportTypes: record.getAvailableReportTypes(),
      maxVersion: record.getLatestVersion("general") || 0,
    };
  }
}

/**
 * UI ViewModel for Record details page
 */
